import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, startOfWeek, endOfWeek, startOfMonth, startOfYear } from "date-fns";
import { saveAs } from "file-saver";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import { useApiCentral } from "../../../api/api-central";
import { useAlert } from "../../../services/alert";
import InputAutocomplete from "../inputs/input-autocomplete";
import InputDatetime from "../inputs/input-datetime";
import InputSelect from "../inputs/input-select";
const REPORT_TYPES = [
    "queue_calls_statistics",
    "detailed_queue_statistics",
    "agent_calls_statistics",
    "agents_in_queue_statistics",
];
const dayBounds = (dateStr, bound) => `${dateStr} ${bound === "start" ? "00:00:00" : "23:59:59"}`;
const getIntervalDates = (timeFrame) => {
    if (timeFrame === "custom")
        return null;
    const curr = new Date();
    if (timeFrame === "day") {
        const today = format(curr, "yyyy-MM-dd");
        return { start: dayBounds(today, "start"), end: dayBounds(today, "end") };
    }
    if (timeFrame === "yesterday") {
        const yesterday = format(new Date(curr.getTime() - 86400000), "yyyy-MM-dd");
        return { start: dayBounds(yesterday, "start"), end: dayBounds(yesterday, "end") };
    }
    if (timeFrame === "this_week") {
        const start = format(startOfWeek(curr, { weekStartsOn: 1 }), "yyyy-MM-dd");
        const end = format(curr, "yyyy-MM-dd");
        return { start: dayBounds(start, "start"), end: dayBounds(end, "end") };
    }
    if (timeFrame === "last_week") {
        const sevenDaysBefore = new Date(new Date().setDate(new Date().getDate() - 7));
        const start = format(startOfWeek(sevenDaysBefore, { weekStartsOn: 1 }), "yyyy-MM-dd");
        const end = format(endOfWeek(sevenDaysBefore, { weekStartsOn: 1 }), "yyyy-MM-dd");
        return { start: dayBounds(start, "start"), end: dayBounds(end, "end") };
    }
    if (timeFrame === "this_month") {
        const start = format(startOfMonth(curr), "yyyy-MM-dd");
        const end = format(curr, "yyyy-MM-dd");
        return { start: dayBounds(start, "start"), end: dayBounds(end, "end") };
    }
    if (timeFrame === "last_month") {
        const firstDay = format(new Date(curr.getFullYear(), curr.getMonth() - 1, 1), "yyyy-MM-dd");
        const lastDay = format(new Date(curr.getFullYear(), curr.getMonth(), 0), "yyyy-MM-dd");
        return { start: dayBounds(firstDay, "start"), end: dayBounds(lastDay, "end") };
    }
    if (timeFrame === "this_year") {
        const start = format(startOfYear(curr), "yyyy-MM-dd");
        const end = format(curr, "yyyy-MM-dd");
        return { start: dayBounds(start, "start"), end: dayBounds(end, "end") };
    }
    if (timeFrame === "last_year") {
        const firstDay = format(new Date(curr.getFullYear() - 1, 0, 1), "yyyy-MM-dd");
        const lastDay = format(new Date(curr.getFullYear(), 0, 0), "yyyy-MM-dd");
        return { start: dayBounds(firstDay, "start"), end: dayBounds(lastDay, "end") };
    }
    return null;
};
const queueKey = (queue) => `${queue.queue_number}|${queue.queue_name}`;
const agentKey = (agent) => `${agent.extension}|${agent.name}`;
const sortQueuesByName = (a, b) => a.queue_name.localeCompare(b.queue_name, undefined, { sensitivity: "base" });
const sortAgentsByName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
export default function ThreeCxDownloadsModal({ open, onClose }) {
    const { t } = useTranslation();
    const api = useApiCentral();
    const alert = useAlert();
    const [reportType, setReportType] = useState("queue_calls_statistics");
    const [timeFrame, setTimeFrame] = useState("last_month");
    const defaultInterval = getIntervalDates("last_month");
    const [intervalStartDate, setIntervalStartDate] = useState(defaultInterval.start);
    const [intervalEndDate, setIntervalEndDate] = useState(defaultInterval.end);
    const [selectedQueues, setSelectedQueues] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [excludeWelcomeMessage, setExcludeWelcomeMessage] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [queues, setQueues] = useState([]);
    const [agents, setAgents] = useState([]);
    const [queuesLoading, setQueuesLoading] = useState(false);
    const [agentsLoading, setAgentsLoading] = useState(false);
    useEffect(() => {
        if (timeFrame === "custom")
            return;
        const dates = getIntervalDates(timeFrame);
        if (!dates)
            return;
        setIntervalStartDate(dates.start);
        setIntervalEndDate(dates.end);
    }, [timeFrame]);
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        setTimeFrame("last_month");
        setQueues([]);
        setAgents([]);
        setSelectedQueues([]);
        setSelectedAgents([]);
        setQueuesLoading(true);
        setAgentsLoading(true);
        api.GET_centralThreeCxQueues()
            .then(response => {
            if (cancelled)
                return;
            setQueues([...response.data.data].sort(sortQueuesByName));
        })
            .catch(error => {
            if (cancelled)
                return;
            console.error(error);
            setQueues([]);
            alert.show(t("reports.3cx.queues-load-error"), "error");
        })
            .finally(() => {
            if (cancelled)
                return;
            setQueuesLoading(false);
        });
        api.GET_centralThreeCxAgents()
            .then(response => {
            if (cancelled)
                return;
            setAgents([...response.data.data].sort(sortAgentsByName));
        })
            .catch(error => {
            if (cancelled)
                return;
            console.warn("Central 3CX agents endpoint unavailable", error);
            setAgents([]);
        })
            .finally(() => {
            if (cancelled)
                return;
            setAgentsLoading(false);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const reportTypeOptions = useMemo(() => REPORT_TYPES.map(type => ({
        value: type,
        label: t(`reports.3cx.report-types.${type}`),
    })), [t]);
    const timeFrameOptions = useMemo(() => [
        { value: "day", label: t("metrics.range-options.today") },
        { value: "yesterday", label: t("metrics.range-options.yesterday") },
        { value: "this_week", label: t("metrics.range-options.this-week") },
        { value: "last_week", label: t("metrics.range-options.last-week") },
        { value: "this_month", label: t("metrics.range-options.this-month") },
        { value: "last_month", label: t("metrics.range-options.last-month") },
        { value: "this_year", label: t("metrics.range-options.this-year") },
        { value: "last_year", label: t("metrics.range-options.last-year") },
        { value: "custom", label: t("metrics.range-options.custom.custom") },
    ], [t]);
    const queueOptions = useMemo(() => queues.map(q => ({
        value: queueKey(q),
        label: `${q.queue_name} (${q.queue_number})`,
    })), [queues]);
    const agentOptions = useMemo(() => agents.map(a => ({
        value: agentKey(a),
        label: `${a.name} (${a.extension})`,
    })), [agents]);
    const isValid = Boolean(intervalStartDate) &&
        Boolean(intervalEndDate) &&
        new Date(intervalEndDate) >= new Date(intervalStartDate);
    const submit = (event) => {
        event.preventDefault();
        if (!isValid) {
            alert.show(t("reports.3cx.invalid-date-range"), "error");
            return;
        }
        setIsDownloading(true);
        api.DOWNLOAD_centralThreeCxReport(reportType, intervalStartDate, intervalEndDate, selectedQueues.length > 0 ? selectedQueues : undefined, selectedAgents.length > 0 ? selectedAgents : undefined, excludeWelcomeMessage)
            .then(response => {
            saveAs(new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }), `3CX ${t(`reports.3cx.report-types.${reportType}`)} ${intervalStartDate} - ${intervalEndDate}.xlsx`);
            alert.show(t("reports.3cx.downloaded"), "success");
            onClose();
        })
            .catch(error => {
            console.error(error);
            alert.show(t("reports.3cx.download-error"), "error");
        })
            .finally(() => {
            setIsDownloading(false);
        });
    };
    return (_jsxs(Dialog, Object.assign({ open: open, onClose: onClose, fullWidth: true, maxWidth: "md" }, { children: [_jsx(DialogTitle, { children: t("reports.3cx.download") }), _jsx(DialogContent, Object.assign({ dividers: true }, { children: _jsxs(Grid, Object.assign({ container: true, spacing: 3 }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12, mt: 2 }, { children: _jsx(InputSelect, { label: t("reports.3cx.report-type"), required: false, options: reportTypeOptions, value: reportType, setValue: value => setReportType(value) }) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(InputSelect, { label: t("tickets.ticket-filters.interval"), required: false, options: timeFrameOptions, value: timeFrame, setValue: value => setTimeFrame(value) }) })), timeFrame === "custom" && (_jsxs(_Fragment, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12, md: 6 }, { children: _jsx(InputDatetime, { type: "date_time", label: t("reports.3cx.interval-start-date"), required: true, status: "start", value: intervalStartDate, setValue: setIntervalStartDate }) })), _jsx(Grid, Object.assign({ item: true, xs: 12, md: 6 }, { children: _jsx(InputDatetime, { type: "date_time", label: t("reports.3cx.interval-end-date"), required: true, status: "end", value: intervalEndDate, setValue: setIntervalEndDate }) }))] })), _jsx(Grid, Object.assign({ item: true, xs: 12, md: 6 }, { children: _jsx(InputAutocomplete, { label: t("reports.3cx.queues"), required: false, multiple: true, loading: queuesLoading, options: queueOptions, value: selectedQueues, setValue: setSelectedQueues, disabled: !queuesLoading && queueOptions.length === 0 }) })), _jsx(Grid, Object.assign({ item: true, xs: 12, md: 6 }, { children: _jsx(InputAutocomplete, { label: t("reports.3cx.agents"), required: false, multiple: true, loading: agentsLoading, options: agentOptions, value: selectedAgents, setValue: setSelectedAgents, disabled: !agentsLoading && agentOptions.length === 0 }) })), _jsx(Grid, Object.assign({ item: true, xs: 12, pt: 2 }, { children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: excludeWelcomeMessage, onChange: event => setExcludeWelcomeMessage(event.target.checked) }), label: t("reports.3cx.exclude-welcome-message") }) }))] })) })), _jsx(DialogActions, Object.assign({ sx: { p: 3 } }, { children: _jsx(LoadingButton, Object.assign({ color: "secondary", variant: "contained", loading: isDownloading, disabled: !isValid, onClick: submit }, { children: t("reports.3cx.download") })) }))] })));
}
